import { useRouter } from 'next/router';
import { useEffect, type ReactNode } from 'react';

import { useAuth } from '@/context/auth.context';

const PUBLIC_ROUTES = ['/login'] as const;

type AuthState = ReturnType<typeof useAuth>['auth'];
type AuthLoadingState = ReturnType<typeof useAuth>['loading'];
type RouteRedirectPath = '/' | '/login' | null;

function isPublicPath(pathname: string) {
  return PUBLIC_ROUTES.some((route) => route === pathname);
}

function getRouteRedirectPath({
  auth,
  isDataHealthRoute,
  isProcessorsRoute,
  isPublicRoute,
}: {
  auth: AuthState;
  isDataHealthRoute: boolean;
  isProcessorsRoute: boolean;
  isPublicRoute: boolean;
}): RouteRedirectPath {
  const canAccessDataHealth = !auth?.selectedGroup?.government && Boolean(auth?.user?.tester);

  const redirectRules = [
    { matches: !auth && !isPublicRoute, path: '/login' },
    { matches: Boolean(auth && isPublicRoute), path: '/' },
    { matches: Boolean(auth && isProcessorsRoute && !auth.selectedGroup?.explore_processors), path: '/' },
    { matches: Boolean(auth && isDataHealthRoute && !canAccessDataHealth), path: '/' },
  ] satisfies Array<{ matches: boolean; path: NonNullable<RouteRedirectPath> }>;

  return redirectRules.find(({ matches }) => matches)?.path ?? null;
}

function shouldRenderRoute({
  isPublicRoute,
  loading,
  redirectPath,
}: {
  isPublicRoute: boolean;
  loading: AuthLoadingState;
  redirectPath: RouteRedirectPath;
}) {
  if (redirectPath) return false;
  if (loading) return isPublicRoute;

  return true;
}

export function ClientRouteGuard({ children }: { children: ReactNode }) {
  const router = useRouter();
  const { auth, loading } = useAuth();

  const isPublicRoute = isPublicPath(router.pathname);
  const isProcessorsRoute = router.pathname.startsWith('/processors');
  const isDataHealthRoute = router.pathname.startsWith('/data-health');
  const redirectPath = loading
    ? null
    : getRouteRedirectPath({
        auth,
        isDataHealthRoute,
        isProcessorsRoute,
        isPublicRoute,
      });

  useEffect(() => {
    if (redirectPath) {
      void router.replace(redirectPath);
    }
  }, [redirectPath, router]);

  const shouldRender = shouldRenderRoute({
    isPublicRoute,
    loading,
    redirectPath,
  });

  return shouldRender ? <>{children}</> : null;
}
